import { useEffect, useRef } from 'react';
import { getNowSnapshot } from '@/shared/lib/schedule';
import { playerRepository } from '../../data/playerRepository';
import type { ItemExibicao } from '../../domain/ports/IPlayerRepository';
import type { MidiaCampanha } from '../../domain/usecases/campanhasPlayer';
import { somarExibicao, type BufferExibicoes } from '../../domain/usecases/exibicoesCampanha';

const ENVIO_MS = 5 * 60_000;
const DIA_MS = 60_000;
const TETO_CHAVES = 200;
const MAX_ITENS_LOTE = 50;

interface Pendente {
  lote_id: string;
  itens: ItemExibicao[];
}

interface Persistencia {
  buffer: BufferExibicoes;
  pendente: Pendente | null;
}

function chaveStorage(telaId: string): string {
  return `ds:exibicoes:${telaId}`;
}

function lerStorage(telaId: string): Persistencia {
  const raw = localStorage.getItem(chaveStorage(telaId));
  if (!raw) return { buffer: {}, pendente: null };
  const parsed = JSON.parse(raw) as Partial<Persistencia>;
  if (!parsed || typeof parsed.buffer !== 'object' || parsed.buffer === null) return { buffer: {}, pendente: null };
  return { buffer: parsed.buffer, pendente: parsed.pendente ?? null };
}

function limitar(buf: BufferExibicoes): BufferExibicoes {
  const chaves = Object.keys(buf);
  if (chaves.length <= TETO_CHAVES) return buf;
  const ordenadas = [...chaves].sort((a, b) => buf[a].dia.localeCompare(buf[b].dia) || a.localeCompare(b));
  const segue = { ...buf };
  for (const chave of ordenadas.slice(0, chaves.length - TETO_CHAVES)) delete segue[chave];
  return segue;
}

/** Início do dia local seguinte, em ms. Um trecho que cruza essa marca é dividido. */
function inicioProximoDiaLocal(ms: number): number {
  const data = new Date(ms);
  return new Date(data.getFullYear(), data.getMonth(), data.getDate() + 1).getTime();
}

export function useRegistrarExibicoes(params: {
  telaId: string | undefined;
  /** Item de campanha em tela agora, ou null. */
  itemAtual: MidiaCampanha | null;
  /** Falso com aba oculta, Momento Especial, captura/transmissão ou barra fechada. */
  visivel: boolean;
}): void {
  const cbRef = useRef(params);
  useEffect(() => { cbRef.current = params; });

  const estadoRef = useRef<Persistencia>({ buffer: {}, pendente: null });
  const inicioRef = useRef<number | null>(null);
  // Duração medida no relógio monotônico: ajuste do relógio da TV (NTP) não apaga nem infla o trecho.
  const inicioMonoRef = useRef(0);
  const campanhaRef = useRef<string | null>(null);
  const sincronizarRef = useRef<() => void>(() => {});

  useEffect(() => {
    const telaId = params.telaId;
    if (!telaId) return;

    try {
      estadoRef.current = lerStorage(telaId);
    } catch {
      estadoRef.current = { buffer: {}, pendente: null };
    }

    function persistir() {
      const id = cbRef.current.telaId;
      if (!id) return;
      try {
        localStorage.setItem(chaveStorage(id), JSON.stringify(estadoRef.current));
      } catch { /* quota ou modo privado: a TV continua */ }
    }

    persistir();

    /** Fecha o trecho aberto agora; o fim é início + duração monotônica (o dia vem do relógio de parede). */
    function fecharAgora() {
      const inicio = inicioRef.current;
      const campanha = campanhaRef.current;
      const fim = inicio == null ? 0 : inicio + Math.max(0, performance.now() - inicioMonoRef.current);
      if (inicio == null || !campanha || fim <= inicio) {
        inicioRef.current = null;
        campanhaRef.current = null;
        return;
      }
      inicioRef.current = null;
      campanhaRef.current = null;
      let cursor = inicio;
      while (cursor < fim) {
        const corte = Math.min(fim, inicioProximoDiaLocal(cursor));
        if (corte <= cursor) break;
        const dia = getNowSnapshot(new Date(cursor)).dateStr;
        estadoRef.current.buffer = somarExibicao(
          estadoRef.current.buffer, campanha, dia, (corte - cursor) / 1000,
        );
        cursor = corte;
      }
      estadoRef.current.buffer = limitar(estadoRef.current.buffer);
      persistir();
    }

    function campanhaVisivel(): string | null {
      const atual = cbRef.current;
      if (!atual.visivel || document.visibilityState === 'hidden') return null;
      return atual.itemAtual?.campanha_id ?? null;
    }

    function sincronizar() {
      try {
        const agora = Date.now();
        const campanha = campanhaVisivel();
        const diaAberto = inicioRef.current == null ? null : getNowSnapshot(new Date(inicioRef.current)).dateStr;
        const diaAgora = getNowSnapshot(new Date(agora)).dateStr;
        if (inicioRef.current != null && campanhaRef.current && (campanha !== campanhaRef.current || diaAgora !== diaAberto)) {
          fecharAgora();
        }
        if (campanha && inicioRef.current == null) {
          inicioRef.current = agora;
          inicioMonoRef.current = performance.now();
          campanhaRef.current = campanha;
        }
      } catch { /* a contagem nunca interrompe a TV */ }
    }

    let enviando = false;
    async function enviar() {
      if (enviando) return;
      const id = cbRef.current.telaId;
      if (!id) return;
      enviando = true;
      try {
        for (;;) {
          if (!estadoRef.current.pendente) {
            // A RPC recusa lote com mais de 50 itens; o resto fica no buffer para a próxima volta do laço.
            const chaves = Object.keys(estadoRef.current.buffer).slice(0, MAX_ITENS_LOTE);
            if (chaves.length === 0) return;
            const resto = { ...estadoRef.current.buffer };
            const itens = chaves.map((chave) => { const item = resto[chave]; delete resto[chave]; return item; });
            estadoRef.current = {
              buffer: resto,
              pendente: { lote_id: crypto.randomUUID(), itens },
            };
            persistir();
          }
          const pendente = estadoRef.current.pendente;
          if (!pendente) return;
          await playerRepository.registrarExibicoes(id, pendente.lote_id, pendente.itens);
          estadoRef.current = { ...estadoRef.current, pendente: null };
          persistir();
        }
      } catch { /* lote pendente permanece para a próxima tentativa */ }
      finally { enviando = false; }
    }

    sincronizarRef.current = sincronizar;
    sincronizar();

    const diaTimer = setInterval(sincronizar, DIA_MS);
    const envioTimer = setInterval(() => { void enviar(); }, ENVIO_MS);
    function aoMudarAba() {
      try {
        sincronizar();
        if (document.visibilityState === 'visible') void enviar();
      } catch { /* a contagem nunca interrompe a TV */ }
    }
    document.addEventListener('visibilitychange', aoMudarAba);

    return () => {
      clearInterval(diaTimer);
      clearInterval(envioTimer);
      document.removeEventListener('visibilitychange', aoMudarAba);
      sincronizarRef.current = () => {};
      try { fecharAgora(); } catch { /* a contagem nunca interrompe a TV */ }
    };
  }, [params.telaId]);

  useEffect(() => {
    try { sincronizarRef.current(); } catch { /* a contagem nunca interrompe a TV */ }
  });
}
